import React, { useEffect, useState } from "react";
import { usersAPI } from "../../utils/usersAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";

function AssignAnalyst() {
  const [analysts, setAnalysts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({ analyst: "", organization: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const extractErrorMessage = (err) => {
    if (err?.details) {
      if (typeof err.details === "string") return err.details;
      if (Array.isArray(err.details)) return err.details[0];
      if (err.details.organization?.[0]) return err.details.organization[0];
      if (err.details.analyst?.[0]) return err.details.analyst[0];
      if (err.details.non_field_errors?.[0]) {
        return err.details.non_field_errors[0];
      }
    }
    return err?.message || "Failed to assign analyst";
  };

  const loadData = async () => {
    try {
      const [usersResponse, orgsResponse, assignmentsResponse] =
        await Promise.all([
          usersAPI.list({ page_size: 100 }),
          organizationsAPI.list({ page_size: 100 }),
          organizationsAPI.assignments.list({ page_size: 100 }),
        ]);

      const usersData = usersResponse?.data || usersResponse;
      const orgsData = orgsResponse?.data || orgsResponse;
      const assignmentsData = assignmentsResponse?.data || assignmentsResponse;

      const usersList = Array.isArray(usersData)
        ? usersData
        : usersData?.results || [];
      const orgsList = Array.isArray(orgsData)
        ? orgsData
        : orgsData?.results || [];
      const assignmentsList = Array.isArray(assignmentsData)
        ? assignmentsData
        : assignmentsData?.results || [];

      setAnalysts(usersList.filter((user) => user.role === "ANALYST"));
      setOrganizations(orgsList);
      setAssignments(assignmentsList);
      setError("");
    } catch (err) {
      const message = extractErrorMessage(err);
      setError(message);
      showToast.error(message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.analyst || !form.organization) {
      showToast.warning("Please select both analyst and organization");
      return;
    }

    try {
      setLoading(true);
      await organizationsAPI.assignments.create({
        analyst: form.analyst,
        organization: form.organization,
      });
      showToast.success("Analyst assigned successfully");
      setForm({ analyst: "", organization: "" });
      setError("");
      loadData();
    } catch (err) {
      const message = extractErrorMessage(err);
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    if (!assignment?.id) return;
    const confirmed = await showConfirm({
      title: "Remove assignment?",
      message:  
        "This will unassign the analyst from this organization. They will no longer see this client in their portal.",
      confirmText: "Remove",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;

    try {
      setRemovingId(assignment.id);
      await organizationsAPI.assignments.remove(assignment.id);
      showToast.success("Assignment removed successfully");
      await loadData();
    } catch (err) {
      const message = extractErrorMessage(err);
      showToast.error(message);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="page-content">
      <div className="card" style={{ maxWidth: "640px" }}>
        <h2 className="card-title">Assign Analyst to Organization</h2>
        {error && (
          <div className="alert alert-error" style={{ marginTop: "12px" }}>
            {error}
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            marginTop: "24px",
          }}
        >
          <div className="form-group">
            <label>Analyst</label>
            <select
              value={form.analyst}
              onChange={(e) => setForm({ ...form, analyst: e.target.value })}
              required
            >
              <option value="">Select Analyst</option>
              {analysts.map((analyst) => (
                <option key={analyst.id} value={analyst.id}>
                  {analyst.first_name} {analyst.last_name} ({analyst.email})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Organization</label>
            <select
              value={form.organization}
              onChange={(e) =>
                setForm({ ...form, organization: e.target.value })
              }
              required
            >
              <option value="">Select Organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Assigning..." : "Assign Analyst"}
          </button>
        </form>
      </div>

      <div className="card" style={{ marginTop: "24px" }}>
        <h3 className="card-title">Current Assignments</h3>
        {assignments.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Analyst</th>
                <th>Organization</th>
                <th>Assigned At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((assignment) => (
                <tr key={assignment.id}>
                  <td>
                    {assignment.analyst_name ||
                      assignment.analyst_email ||
                      assignment.analyst}
                  </td>
                  <td>
                    {assignment.client_organization_name ||
                      assignment.organization}
                  </td>
                  <td>
                    {assignment.assigned_at
                      ? new Date(assignment.assigned_at).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleRemoveAssignment(assignment)}
                      disabled={removingId === assignment.id}
                    >
                      {removingId === assignment.id ? "Removing..." : "Remove"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ padding: "16px", textAlign: "center" }}>
            No assignments found.
          </p>
        )}
      </div>
    </div>
  );
}

export default AssignAnalyst;
